import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// A section counts as current once its top passes this line (the bar height
// plus a little slack), so the tab switches when the heading reaches the bar.
const ACTIVE_LINE_PX = 96;

const useActiveSection = (ids, enabled) => {
  // A string key keeps the effect stable across renders that rebuild the same list.
  const idsKey = ids.join(',');
  const [activeId, setActiveId] = useState(ids[0]);

  useEffect(() => {
    if (!enabled) return undefined;

    const sectionIds = idsKey.split(',');
    let frame = null;
    const update = () => {
      frame = null;
      const passed = sectionIds.filter((id) => {
        const element = document.getElementById(id);
        return element && element.getBoundingClientRect().top <= ACTIVE_LINE_PX;
      });
      setActiveId(passed[passed.length - 1] ?? sectionIds[0]);
    };
    const onScroll = () => {
      if (frame === null) frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [idsKey, enabled]);

  return activeId;
};

/**
 * Bar shown once the picker has scrolled out of view. Desktop: anchor tabs,
 * selection summary and the buy action. Mobile: fixed to the bottom, summary
 * and buy action only. The action is the same element the top section
 * renders, so the bar never has buy logic of its own.
 */
const StickyProductBar = ({ visible, tabs, summary, price, action }) => {
  const activeId = useActiveSection(
    tabs.map((tab) => tab.id),
    visible,
  );

  return (
    <div
      className={classNames('pdp-sticky-bar', { visible })}
      aria-hidden={!visible}
      inert={visible ? undefined : ''}
    >
      <div className="container pdp-sticky-bar-inner">
        <nav className="pdp-sticky-tabs" aria-label="Product page sections">
          {tabs.map((tab) => (
            <a
              key={tab.id}
              href={`#${tab.id}`}
              className={classNames('pdp-sticky-tab', { active: tab.id === activeId })}
              aria-current={tab.id === activeId ? 'location' : undefined}
            >
              {tab.label}
            </a>
          ))}
        </nav>
        <div className="pdp-sticky-buy">
          <div className="pdp-sticky-summary">
            <span className="pdp-sticky-summary-name">{summary}</span>
            {price}
          </div>
          <div className="pdp-sticky-action">{action}</div>
        </div>
      </div>
    </div>
  );
};

StickyProductBar.propTypes = {
  visible: PropTypes.bool.isRequired,
  tabs: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, label: PropTypes.string }))
    .isRequired,
  summary: PropTypes.string,
  price: PropTypes.node,
  action: PropTypes.node,
};

export default StickyProductBar;
